import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StaticDataService } from '@dp/services/static-data.service';
import { AuthService } from 'app/auth/auth.service';
import { UIService } from 'app/shared';
import { environment } from 'environments/environment';
import { MetricFormAddComponent } from './metric-form-add/metric-form-add.component';
import { NmApiService } from './nm-service';
import { INmState } from './nm-service';
import { INotificationMetrics } from './nm-model';
import { UserAccessService } from 'app/auth/user-access.service';
import { USER_ACCESS } from 'app/user-access.constants';
import { Utility } from '@dp/utilities';

interface ICreateRequestPayload {
  mediumSeverityToleranceSecs: number | null;
  highSeverityToleranceSecs: number | null;
  toleranceSecs?: number | null;
  notificationCode?: 'CN_EXTENDED_POO_STORAGE' | 'CN_EXTENDED_POD_STORAGE' | string;
  portCode?: string;
  carrier?: string;
  carrierScac?: string;
  demurrageFreeDays?: number;
  detentionFreeDays?: number;
}

@Component({
  selector: 'dp-notification-metrics',
  templateUrl: './notification-metrics.component.html',
  styleUrls: ['./notification-metrics.component.scss'],
})
export class NotificationMetricsComponent implements OnInit {
  isAdmin: boolean;

  vm$ = this.nmApiService.vm$;
  // ports = ports; // sample ports
  ports = [];
  deletedIds = new Set<number>();
  editingIds = new Set<number>();
  currentMode: 'ADD_POO' | 'ADD_POD' | '' = '';
  isDemurrageDetentionEnabled: boolean;

  constructor(
    private nmApiService: NmApiService,
    private uiService: UIService,
    private staticDataService: StaticDataService,
    private authService: AuthService,
    private dialog: MatDialog,
    public userAccessService: UserAccessService
  ) {
    this.isAdmin = this.userAccessService.hasAccess(
      USER_ACCESS.DEMURRAGE_CONFIG_AND_HOURS_BEFORE_ARRIVAL,
      null,
      this.authService.currentUserValue.isAdmin
    );
  }

  ngOnInit(): void {
    const staticData = this.staticDataService.getStaticDataDirect();
    this.ports = staticData.ports;
    this.isDemurrageDetentionEnabled = this.authService?.currentUserValue?.accountCapabilities?.isDemurrageDetentionEnabled || false;
    // console.log('The ports: ', this.ports);
    // this.openCustomizePortDlg('ADD_POD');
  }

  createDefaultFormData(notificationCode: string): ICreateRequestPayload {
    return {
      mediumSeverityToleranceSecs: null,
      highSeverityToleranceSecs: null,
      demurrageFreeDays: null,
      detentionFreeDays: null,
      toleranceSecs: null,
      notificationCode: notificationCode,
      portCode: '',
      carrier: '',
      carrierScac: '',
    };
  }

  getNotificationMetric(vm: INmState, code: string): INotificationMetrics {
    if (vm && Array.isArray(vm.notificationMetrics)) {
      return vm.notificationMetrics.find((item) => item.notificationCode === code);
    }
    return null;
  }

  createNotificationMetric(request): void {
    const { notificationCode } = request.options;
    const {
      mediumSeverityToleranceSecs,
      highSeverityToleranceSecs,
      demurrageFreeDays,
      detentionFreeDays,
      toleranceSecs,
      portCode,
      carrier,
      carrierScac,
    } = request.data;

    let payload: ICreateRequestPayload = {
      notificationCode,
      mediumSeverityToleranceSecs: Utility.daysToSeconds(mediumSeverityToleranceSecs),
      highSeverityToleranceSecs: Utility.daysToSeconds(highSeverityToleranceSecs),
      demurrageFreeDays: Utility.daysToSeconds(demurrageFreeDays),
      detentionFreeDays: Utility.daysToSeconds(detentionFreeDays),
      toleranceSecs,
      portCode,
      carrier: carrierScac,
    };
    if (!this.isDemurrageDetentionEnabled) {
      delete payload.demurrageFreeDays;
      delete payload.detentionFreeDays;
    }
    this.nmApiService.createNotificationMetrics(payload).subscribe(
      (result) => {
        // error result null
        if (!result) {
          this.uiService.showSnackbar('Failed to created notification metric ', null, {
            duration: environment.snackBarDuration.warning,
            panelClass: 'warn',
          });
          return;
        }
        this.uiService.showSnackbar('Successfully created notification metric ', null, {
          duration: environment.snackBarDuration.success,
          panelClass: 'accent',
        });

        // update UI
        this.currentMode = '';
        let currentState = this.nmApiService.currentState();
        let metrics = this.getNotificationMetric(currentState, payload.notificationCode);
        if (metrics && metrics.notificationMetrics) {
          metrics.notificationMetrics.unshift(result);
          // this.nmApiService.update(currentState);
        }
      },
      (error) => {
        console.log('error: ', error);

        this.uiService.showSnackbar('Failed to created notification metric ', null, {
          duration: environment.snackBarDuration.warning,
          panelClass: 'warning',
        });
      }
    );
  }

  updateNotificationMetric(request): void {
    console.log(request);
    // add demurrageFreeDays,  detentionFreeDays
    const {
      id,
      mediumSeverityToleranceSecs,
      highSeverityToleranceSecs,
      demurrageFreeDays,
      detentionFreeDays,
      portCode,
      carrier,
      carrierScac,
    } = request.data;

    let payload: ICreateRequestPayload = {
      mediumSeverityToleranceSecs: Utility.daysToSeconds(mediumSeverityToleranceSecs),
      highSeverityToleranceSecs: Utility.daysToSeconds(highSeverityToleranceSecs),
      demurrageFreeDays: Utility.daysToSeconds(demurrageFreeDays),
      detentionFreeDays: Utility.daysToSeconds(detentionFreeDays),
    };
    if (!this.isDemurrageDetentionEnabled) {
      delete payload.demurrageFreeDays;
      delete payload.detentionFreeDays;
    }
    this.nmApiService.updateNotificationMetrics(id, payload).subscribe(
      (result) => {
        console.log('update result: ', result);

        // error result null
        if (!result) {
          this.uiService.showSnackbar('Failed to update notification metric ', null, {
            duration: environment.snackBarDuration.warning,
            panelClass: 'warn',
          });
          return;
        }
        this.uiService.showSnackbar('Successfully updated notification metric ', null, {
          duration: environment.snackBarDuration.success,
          panelClass: 'accent',
        });
      },
      (error) => {
        console.log('error: ', error);

        this.uiService.showSnackbar('Failed to update notification metric ', null, {
          duration: environment.snackBarDuration.warning,
          panelClass: 'warning',
        });
      }
    );
  }

  deleteNotificationMetric(metricId: number): void {
    this.nmApiService.deleteNotificationMetric(metricId).subscribe((result) => {
      this.deletedIds.add(metricId);
      this.uiService.showSnackbar('A port customization is deleted!', null, {
        duration: environment.snackBarDuration.warning,
        panelClass: 'accent',
      });
    });
  }

  getToleranceSecsInDay(metric): boolean {
    let hours = metric.toleranceSecs / 3600;
    metric.toleranceSecs = hours;
    return true;
  }

  onDispatch($event: any) {
    let mode = $event?.options?.mode; // 'CREATE' | 'UPDATE'

    switch (mode) {
      case 'CREATE':
        this.createNotificationMetric($event);
        break;
      case 'UPDATE':
        console.log('UPDATE', $event);
        // if no id, create a default
        if ($event.data.id === null) {
          // will create default metric
          this.createDefaultNotificationMetric($event);
        } else {
          this.updateNotificationMetric($event);
        }
        break;
      case 'DELETE':
        console.log('delete');
        this.deleteNotificationMetric($event.data.id);
        break;
      case 'CLEAR':
        this.currentMode = '';
        // this.editingIds.delete($event.data.id);
        break;
      case 'EDIT':
        this.editingIds.add($event.data.id);
        break;

      default:
        console.log('default action');
    }
  }
  createDefaultNotificationMetric(request): void {
    console.log('create default metric');
    console.log(request);

    const { notificationCode } = request.options;
    const {
      mediumSeverityToleranceSecs,
      highSeverityToleranceSecs,
      demurrageFreeDays,
      detentionFreeDays,
      toleranceSecs,
      portCode,
      carrier,
      carrierScac,
    } = request.data;

    let payload: ICreateRequestPayload = {
      notificationCode,
      mediumSeverityToleranceSecs: mediumSeverityToleranceSecs * 86400,
      highSeverityToleranceSecs: highSeverityToleranceSecs * 86400,
      demurrageFreeDays: Utility.daysToSeconds(demurrageFreeDays),
      detentionFreeDays: Utility.daysToSeconds(detentionFreeDays),
      toleranceSecs,
      portCode,
      carrier: carrierScac,
    };
    if (!this.isDemurrageDetentionEnabled) {
      delete payload.demurrageFreeDays;
      delete payload.detentionFreeDays;
    }
    this.nmApiService.createNotificationMetrics(payload).subscribe(
      (result) => {
        console.log('created result: ', result);

        // error result null
        if (!result) {
          this.uiService.showSnackbar('Failed to created notification metric ', null, {
            duration: environment.snackBarDuration.warning,
            panelClass: 'warn',
          });
          return;
        }
        this.uiService.showSnackbar('Successfully created notification metric ', null, {
          duration: environment.snackBarDuration.success,
          panelClass: 'accent',
        });

        this.currentMode = '';
        let currentState = this.nmApiService.currentState();
        let metrics = this.getNotificationMetric(currentState, payload.notificationCode);
        if (metrics && metrics.notificationMetrics) {
          metrics.notificationMetrics.shift();
          metrics.notificationMetrics.unshift(result);
        }
      },
      (error) => {
        console.log('error: ', error);

        this.uiService.showSnackbar('Failed to created notification metric ', null, {
          duration: environment.snackBarDuration.warning,
          panelClass: 'warning',
        });
      }
    );
  }

  // ARRIVING_SOON notification
  toleranceHoursChange($event, notificationMetrics) {
    if (!$event) return;

    if ($event.target.value) {
      console.log($event.target.value);
    }

    if (!notificationMetrics.id) {
      let request = {
        toleranceSecs: $event.target.value * 3600,
        notificationCode: 'ARRIVING_SOON',
      };
      // create notificationMetric for ARRIVING_SOON
      this.createToleranceNotificationMetric(request).subscribe(
        (result) => {
          if (!result) {
            this.uiService.showSnackbar('Failed to created notification metric ', null, {
              duration: environment.snackBarDuration.warning,
              panelClass: 'warn',
            });
            return;
          }
          this.uiService.showSnackbar('Successfully updated notification metric ', null, {
            duration: environment.snackBarDuration.success,
            panelClass: 'accent',
          });

          // update UI
          notificationMetrics.id = result.id;
        },
        (error) => {
          this.uiService.showSnackbar('Failed to created notification metric ', null, {
            duration: environment.snackBarDuration.warning,
            panelClass: 'warning',
          });
        }
      );
    } else if (notificationMetrics.id) {
      let request = {
        toleranceSecs: $event.target.value * 3600,
      };

      // update
      this.updateToleranceNotificationMetric(notificationMetrics.id, request).subscribe(
        (result) => {
          if (!result) {
            this.uiService.showSnackbar('Failed to created notification metric ', null, {
              duration: environment.snackBarDuration.warning,
              panelClass: 'warn',
            });
            return;
          }
          this.uiService.showSnackbar('Successfully updated notification metric ', null, {
            duration: environment.snackBarDuration.success,
            panelClass: 'accent',
          });
        },
        (error) => {
          this.uiService.showSnackbar('Failed to created notification metric ', null, {
            duration: environment.snackBarDuration.warning,
            panelClass: 'warning',
          });
        }
      );
    }
  }

  createToleranceNotificationMetric(request): any {
    return this.nmApiService.createNotificationMetrics(request);
  }

  updateToleranceNotificationMetric(id, request): any {
    return this.nmApiService.updateNotificationMetrics(id, request);
  }

  openCustomizePortDlg(mode: string, metrics: INotificationMetrics) {
    const isDemurrageDetentionEnabled = this.authService?.currentUserValue?.accountCapabilities?.isDemurrageDetentionEnabled || false;
    const isDemurrageDetentionCostEnabled = this.authService?.currentUserValue?.accountCapabilities?.isDemurrageDetentionCostEnabled;
    const dlgRef = this.dialog.open(MetricFormAddComponent, {
      width: '500px',
      data: {
        currentMode: this.currentMode,
        formData: this.createDefaultFormData(mode),
        options: {
          notificationCode: metrics.notificationCode,
          ports: this.ports,
        },
        isDemurrageDetentionEnabled,
        isDemurrageDetentionCostEnabled
      },
      panelClass: 'dp-notification-metric-form-add'
    });

    dlgRef.afterClosed().subscribe((result) => {
      this.onDispatch(result);
    });
  }
}
